import { call, put, takeLatest } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import pickBy from 'lodash/pickBy'

import {
  createLearningMaterial,
  getLearningMaterialDetail,
  fetchingLearningMaterialList,
  fetchingLearningMaterialListV2,
  updateLearningMaterial,
  generateLearningMaterial,
  fetchingContentCreatorList,
} from '@store/actions/learningMaterialActions';
import {
  setSuccess,
  setLoading,
  setLearningMaterialList,
  setLearningMaterialCreation,
  patchLearningMaterialCreation,
  setContentCreatorList,
} from '@store/reducers/learningMaterialReducer';

import { resourceService } from '@libs/axios';
import { PayloadAction } from '@reduxjs/toolkit/dist';
import i18n from '@locales/i18n';
import { ResponseType } from '@store/index';
const API_URL = process.env.REACT_APP_API_URL;
const H5P_API_URL = process.env.REACT_H5P_HUB_API_URL;

function* fetchingLearningMaterialListSaga(
  action: ReturnType<typeof fetchingLearningMaterialList>,
) {
  try {
    yield put(setLoading(true));
    const response: ResponseType = yield call(
      resourceService.get,
      API_URL + `/api/submodulesquestions/`,
    );
    if (response.status === 200 && response.data?.count) {
      yield put(setLearningMaterialList(response.data));
    }
  } catch (error) {
    toast.error(i18n.t('material.error.fetchingList'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchingLearningMaterialListSagaV2(
  action: ReturnType<typeof fetchingLearningMaterialListV2>,
) {
  const { filters } = action.payload;
  
  try {
    yield put(setLoading(true));
    const response: ResponseType = yield call(
      resourceService.get,
      API_URL + `/api/submodulesquestionsv2/?` + new URLSearchParams(pickBy(filters)).toString(),
    );
    if (response.status === 200) {
      yield put(setLearningMaterialList(response.data));
    }
  } catch (error) {
    toast.error(i18n.t('material.error.fetchingList'));
  } finally {
    yield put(setLoading(false));
  }
}

function* getLearningMaterialDetailSaga(action: PayloadAction<any>) {
  try {
    yield put(setLoading(true));
    const data = action.payload;
    const response: ResponseType = yield call(
      resourceService.get,
      API_URL + `/api/submodulesquestions/${data.id}/`,
    );
    if (response.status === 200 && response.data) {
      yield put(setLearningMaterialCreation(response.data));
    }
  } catch (error) {
    toast.error(i18n.t('material.error.fetchingDetail'));
  } finally {
    yield put(setLoading(false));
  }
}

function* createLearningMaterialSaga(action: PayloadAction<any>) {
  try {
    yield put(setLoading(true));
    const data = action.payload;
    const response: ResponseType = yield call(
      resourceService.post,
      API_URL + `/api/submodulesquestions/`,
      data,
    );
    if (response.status === 200 && response.data) {
      yield put(setSuccess(true));
      yield put(setLearningMaterialCreation(response.data.data));
      toast.success(i18n.t('material.success.create'));
    }
  } catch (error) {
    toast.error(i18n.t('material.error.create'));
  } finally {
    yield put(setLoading(false));
  }
}

function* updateLearningMaterialSaga(action: PayloadAction<any>) {
  try {
    yield put(setLoading(true));
    const { data, id } = action.payload;
    const response: ResponseType = yield call(
      resourceService.put,
      API_URL + `/api/submodulesquestions/${id}/`,
      data,
    );
    if (response.status === 200 && response.data) {
      yield put(setSuccess(true));
      yield put(setLearningMaterialCreation(response.data));
      toast.success(i18n.t('material.success.update'));
    }
  } catch (error) {
    toast.error(i18n.t('material.error.update'));
  } finally {
    yield put(setLoading(false));
  }
}

function* generateLearningMaterialSaga(action: PayloadAction<any>) {
  try {
    yield put(setLoading(true));
    const { file_url, user } = action.payload;
    const response: ResponseType = yield call(
      resourceService.post,
      H5P_API_URL + `/api/h5p/convert/`,
      {
        file_url, user
      },
    );
    if (response.status === 200 && response.data) {
      yield put(setSuccess(true));
      yield put(
        patchLearningMaterialCreation({
          block_id_question: response.data.id,
        }),
      );
      toast.success(i18n.t('material.success.generate'));
    }
  } catch (error) {
    toast.error(i18n.t('material.error.generate'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchingContentCreatorListSaga(
  action: ReturnType<typeof fetchingContentCreatorList>,
) {
  const { filters } = action.payload;
  try {
    yield put(setLoading(true));
    const response: ResponseType = yield call(
      resourceService.get,
      API_URL + `/api/contentbankcreator/`,
      {
        params: filters,
      },
    );
    if (response.status === 200) {
      yield put(setContentCreatorList(response.data));
    }
  } catch (error) {
    toast.error('Lỗi khi lấy danh sách người tạo');
  } finally {
    yield put(setLoading(false));
  }
}

export default function* learningMaterialSaga() {
  yield takeLatest(
    fetchingLearningMaterialList,
    fetchingLearningMaterialListSaga,
  );
  yield takeLatest(
    fetchingLearningMaterialListV2,
    fetchingLearningMaterialListSagaV2,
  );
  yield takeLatest(createLearningMaterial, createLearningMaterialSaga);
  yield takeLatest(getLearningMaterialDetail, getLearningMaterialDetailSaga);
  yield takeLatest(updateLearningMaterial, updateLearningMaterialSaga);
  yield takeLatest(generateLearningMaterial, generateLearningMaterialSaga);
  yield takeLatest(fetchingContentCreatorList, fetchingContentCreatorListSaga);
}
